<template>
  <v-container class="pt-3 pb-6 px-6">

    <!-- Floating Button -->
    <floating-button @click="save()" save :disabled="invalid" v-if="authUser"/>

    <!-- Page Header -->
    <page-header
      :title="isNew + ' Adjudicación'"
      icon="mdi-format-list-bulleted"
      divider="-"
      :breadcrumbs="breadcrumbs.items"
      refresh
      v-on:refresh="loadContracts()"
    ></page-header>

    <v-row align="center" justify="space-around">
      <v-col cols="12">
        <v-card>
          <!-- Loading Bar -->
          <v-progress-linear
            indeterminate
            v-if="isLoadingData"
          ></v-progress-linear>
          <!-- Content -->
          <v-card-text v-if="authUser">
            <v-container>
              <v-row align="center" justify="space-around">
                <!-- Client ID -->
                <v-col cols="3">
                  <v-autocomplete
                    v-model="contract.client_id"
                    :error-messages="clientIdErrors"
                    :items="clients"
                    item-text="brand_name"
                    item-value="id"
                    label="Nombre comercial"
                    counter="20"
                    maxlength="20"
                    outlined
                    @blur="$v.contract.client_id.$touch()"
                  ></v-autocomplete>
                </v-col>
                <!-- Contract Code -->
                <v-col cols="3">
                  <v-text-field
                    v-model="contract.client_code"
                    :error-messages="clientCodeErrors"
                    label="Código de adjudicación"
                    counter="20"
                    maxlength="20"
                    outlined
                    @blur="$v.contract.client_code.$touch()"
                  ></v-text-field>
                </v-col>
                <!-- Contract Date -->
                <v-col cols="3">
                  <v-dialog
                    ref="dialog"
                    v-model="dialog"
                    :return-value.sync="contract.contract_date"
                    width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        :value="formatDate(contract.contract_date)"
                        label="Fecha de adjudicación"
                        color="primary"
                        class="shrink mr-5"
                        :error-messages="contractDateErrors"
                        :append-icon="contract.contract_date ? 'mdi-close' : ''"
                        @click:append="contract.contract_date = ''"
                        outlined
                        readonly
                        v-on="on"
                        @blur="$v.contract.contract_date.$touch()"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="contract.contract_date" scrollable color="primary" :max="new Date().toISOString().substr(0, 10)">
                      <v-icon class="ml-2" @click="contract.contract_date = ''">mdi-refresh</v-icon>
                      <v-spacer/>
                      <v-btn text @click="dialog = false">Cancelar</v-btn>
                      <v-btn color="primary" dark @click="$refs.dialog.save(contract.contract_date)">OK</v-btn>
                    </v-date-picker>
                  </v-dialog>
                </v-col>
                <!-- State -->
                <v-col cols="3">
                  <v-select
                    v-model="contract.state"
                    :error-messages="stateErrors"
                    :items="state"
                    label="Estado"
                    outlined
                    @blur="$v.contract.state.$touch()"
                  >
                    <template v-slot:selection="data">
                      <v-chip
                        style="cursor: pointer"
                        :color="data.item === 'PENDIENTE' ? '' : data.item === 'EN CURSO' ? 'primary' : 'deep-orange'"
                        :class="data.item === 'PENDIENTE' ? '' : 'white--text'"
                      >{{ data.item }}</v-chip>
                    </template>
                  </v-select>
                </v-col>
                <!-- Summary -->
                <v-col cols="12">
                  <v-textarea
                    v-model="contract.summary"
                    :error-messages="summaryErrors"
                    label="Resumen"
                    counter
                    outlined
                    @blur="$v.contract.summary.$touch()"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import FloatingButton from "@/components/FloatingButton";
import PageHeader from "@/components/PageHeader";
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import { ContractAwardsRepository } from "@/api/Repositories";
import Util from "@/util/index";

export default {
  components: {
    FloatingButton,
    PageHeader
  },
  mixins: [validationMixin],
  validations: {
    contract: {
      client_id: { required },
      client_code: { required },
      contract_date: { required },
      state: { required },
      summary: { required },
    }
  },
  data: () => ({
    authUser: false,
    isLoadingData: false,
    dialog: false,
    contract: {
      client_id: '',
      client_code: '',
      contract_date: '',
      state: '',
      summary: ''
    },
    state: ['PENDIENTE', 'EN CURSO', 'FINALIZADA'],
    breadcrumbs: {
      items: [
        {
          text: "Configuración",
          disabled: true
        },
        {
          text: "Adjudicaciones",
          disabled: true
        }
      ]
    },
  }),
  computed: {
    loading: {
      get () {
        return this.$store.state.loading
      },
      set (val) {
        this.$store.commit('loading', val)
      }
    },
    isNew () {
      return this.$route.params.id ? 'Modificar' : 'Añadir'
    },
    invalid () {
      return this.$v.$invalid && this.$v.$dirty
    },
    clients () {
      return this.$store.state.clients.clients
    },
    contracts () {
      return this.$store.state.contracts.contracts
    },
    clientIdErrors () {
      const errors = []
      if (!this.$v.contract.client_id.$dirty) return errors
      !this.$v.contract.client_id.required && errors.push('Obligatorio.')
      return errors
    },
    clientCodeErrors () {
      const errors = []
      if (!this.$v.contract.client_code.$dirty) return errors
      !this.$v.contract.client_code.required && errors.push('Obligatorio.')
      return errors
    },
    contractDateErrors() {
      const errors = [];
      if (!this.$v.contract.contract_date.$dirty) return errors;
      !this.$v.contract.contract_date.required && errors.push("Obligatorio.");
      return errors;
    },
    stateErrors () {
      const errors = []
      if (!this.$v.contract.state.$dirty) return errors
      !this.$v.contract.state.required && errors.push('Obligatorio.')
      return errors
    },
    summaryErrors () {
      const errors = []
      if (!this.$v.contract.summary.$dirty) return errors
      !this.$v.contract.summary.required && errors.push('Obligatorio.')
      return errors
    }
  },
  methods: {
    formatDate(date) {
      return Util.formatDate(date)
    },
    contractList () {
      this.$router.push({ name: "contract-list" });
    },
    save () {
      this.$route.params.id ? this.editContract() : this.createContract()
    },
    createContract () {
      this.$v.$touch()
      if (!this.$v.$invalid ) {
        this.loading = true
        this.$store.dispatch('contracts/createContract', this.contract)
          .then(() => {
            this.$toast("Adjudicación creada correctamente.", {
              color: "success",
              x: "center",
              y: "top",
              icon: "mdi-information"
            });
            this.contractList()
          })
          .catch(error => {
            var errorToastMessage = "Ha ocurrido un error creando la adjudicación."
            Util.showErrorToast(this, error, errorToastMessage);
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    editContract () {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.loading = true
        this.$store.dispatch('contracts/editContract', this.contract)
          .then(() => {
            this.$toast("Adjudicación modificada correctamente.", {
              color: "success",
              x: "center",
              y: "top",
              icon: "mdi-information"
            });
            this.contractList()
          })
          .catch(error => {
            var errorToastMessage = "Ha ocurrido un error modificando la adjudicación."
            Util.showErrorToast(this, error, errorToastMessage);
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    loadContracts () {
      this.$store.dispatch('contracts/getContracts')
        .then(() => {
          this.getContract()
        })
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando las Adjudicaciones."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
    },
    getContract () {
      var id = this.$route.params.id
      if (id) {
        ContractAwardsRepository.get(id)
        .then(response => {
          var data = response.data.data

          this.contract = data
        })
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando el PC autorizado."
            Util.showErrorToast(this, error, errorToastMessage);
          }
          this.contractList();
        })
      } else {
        this.contract = {
          client_id: '',
          client_code: '',
          contract_date: '',
          state: '',
          summary: ''
        }
      }
      this.$v.$reset()
      this.authUser = true
    },
    loadClients () {
      this.isLoadingData = true;
      this.$store.dispatch('clients/getClients')
        .then(() => {
          this.loadContracts()
        })
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando la lista de clientes."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
        .finally(() => {
          this.authUser = true
          this.isLoadingData = false;
          this.$v.$reset()
        });
    }
  },
  created () {
    this.loadClients()
  }
}
</script>
